import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Badge,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSparkles } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { fetchCampaigns, createCampaign, fetchLeads, fetchFlags, generateCampaignFlow } from '../services/api';
import { VALID_STAGES } from '../constants/pipeline';

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp/SMS' },
];
const ARCHETYPE_OPTIONS = [
  { value: 'article-nurture', label: 'Article nurture' },
  { value: 'case-study-sequence', label: 'Case-study sequence' },
  { value: 'event-invite', label: 'Event invite' },
];
const STAGES = VALID_STAGES;

function channelColor(channel) {
  if (channel === 'whatsapp') return 'green';
  if (channel === 'email') return 'blue';
  if (channel === 'sms') return 'orange';
  return 'gray';
}

const STATE_COLORS = {
  draft: 'blue',
  active: 'green',
  paused: 'yellow',
  archived: 'gray',
};
const STATE_ORDER = ['active', 'draft', 'paused', 'archived'];

// Legacy 'completed' campaigns are shown as archived
function campaignState(campaign) {
  const status = campaign.status === 'completed' ? 'archived' : campaign.status;
  return STATE_COLORS[status] ? status : 'draft';
}

function stageCounts(leads, campaignId) {
  const counts = Object.fromEntries(STAGES.map((s) => [s, 0]));
  leads.filter((l) => l.campaignId === campaignId).forEach((l) => {
    if (counts[l.stage] !== undefined) counts[l.stage]++;
  });
  return counts;
}

export default function CampaignsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const [genOpened, { open: openGen, close: closeGen }] = useDisclosure(false);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [name, setName] = useState('');
  const [channel, setChannel] = useState('email');
  const [description, setDescription] = useState('');

  // Campaign generator (beta)
  const [generatorEnabled, setGeneratorEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genArchetype, setGenArchetype] = useState('article-nurture');
  const [genBrief, setGenBrief] = useState('');
  const [genTouchCount, setGenTouchCount] = useState(3);
  const [genEndGoal, setGenEndGoal] = useState('Book a discovery call');
  const [genChannel, setGenChannel] = useState('email');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignsData, leadsData] = await Promise.all([
        fetchCampaigns(api),
        fetchLeads(api, {}),
      ]);
      setCampaigns(campaignsData.campaigns || []);
      setLeads(leadsData.leads || []);
    } catch (err) {
      notifications.show({
        title: 'Failed to load campaigns',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    fetchFlags(api)
      .then((flags) => { if (!cancelled) setGeneratorEnabled(Boolean(flags.campaignGenerator)); })
      .catch(() => { /* flags are optional; button just stays hidden */ });
    return () => { cancelled = true; };
  }, [api]);

  async function handleGenerateFlow(e) {
    e.preventDefault();
    if (!genBrief.trim()) {
      notifications.show({ title: 'Brief is required', message: 'Describe the audience and angle for the campaign.', color: 'red' });
      return;
    }
    setGenerating(true);
    const startedAt = Date.now();
    console.log(`[generator] Requesting campaign flow (${genTouchCount} touches, channel=${genChannel}, brief=${genBrief.trim().length} chars). The LLM designs the skeleton first — this can take several minutes for a complex brief...`);
    try {
      const { campaign } = await generateCampaignFlow(api, {
        archetype: genArchetype,
        brief: genBrief.trim(),
        touchCount: genTouchCount,
        endGoal: genEndGoal.trim(),
        channel: genChannel,
      });
      console.log(`[generator] Flow generated in ${((Date.now() - startedAt) / 1000).toFixed(1)}s: "${campaign.name}" (${campaign.id})`);
      notifications.show({
        title: 'Campaign flow generated',
        message: `"${campaign.name}" created as a draft. Review the flow, then generate assets.`,
        color: 'violet',
      });
      setGenBrief('');
      closeGen();
      navigate(`/campaigns/${campaign.id}`);
    } catch (err) {
      console.error(`[generator] Flow generation failed after ${((Date.now() - startedAt) / 1000).toFixed(1)}s:`, err.response?.data?.error || err.message, err);
      notifications.show({
        title: 'Failed to generate campaign',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setGenerating(false);
    }
  }

  async function handleCreateCampaign(e) {
    e.preventDefault();
    if (!name.trim()) {
      notifications.show({ title: 'Name is required', message: 'Please enter a campaign name.', color: 'red' });
      return;
    }

    setCreating(true);
    try {
      const created = await createCampaign(api, {
        name: name.trim(),
        channel,
        description: description.trim(),
      });
      notifications.show({
        title: 'Campaign created',
        message: 'Your campaign has been added.',
        color: 'teal',
      });
      setName('');
      setChannel('email');
      setDescription('');
      close();
      navigate(`/campaigns/${created.id}`);
    } catch (err) {
      notifications.show({
        title: 'Failed to create campaign',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box p="md" bg="white" h="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={700} size="lg">Campaigns</Text>
          <Group gap="xs">
            {generatorEnabled && (
              <Button
                variant="light"
                color="violet"
                leftSection={<IconSparkles size={14} />}
                rightSection={<Badge size="xs" variant="filled" color="violet">Beta</Badge>}
                onClick={openGen}
              >
                Generate Campaign
              </Button>
            )}
            <Button leftSection={<IconPlus size={14} />} onClick={open}>
              Add Campaign
            </Button>
          </Group>
        </Group>

        {campaigns.length === 0 && !loading ? (
          <Text c="dimmed" ta="center" py="xl">
            No campaigns yet. Create your first one.
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  {STAGES.map((s) => (
                    <Table.Th key={s} style={{ textAlign: 'center' }}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Table.Th>
                  ))}
                  <Table.Th style={{ textAlign: 'center' }}>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...campaigns]
                  .sort((a, b) => STATE_ORDER.indexOf(campaignState(a)) - STATE_ORDER.indexOf(campaignState(b)))
                  .map((c) => {
                    const state = campaignState(c);
                    const counts = stageCounts(leads, c.id);
                    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
                    const dimmed = state === 'archived';
                    return (
                      <Table.Tr key={c.id} style={dimmed ? { opacity: 0.6 } : undefined}>
                        <Table.Td>
                          <Anchor component={Link} to={`/campaigns/${c.id}`} fw={600}>
                            {c.name}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={STATE_COLORS[state]} size="sm">
                            {state}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={channelColor(c.channel)} size="sm">
                            {c.channel === 'whatsapp' ? 'WhatsApp/SMS' : c.channel}
                          </Badge>
                        </Table.Td>
                        {STAGES.map((s) => (
                          <Table.Td key={s} style={{ textAlign: 'center' }}>
                            {counts[s] > 0
                              ? <Text size="sm" fw={500}>{counts[s]}</Text>
                              : <Text size="sm" c="dimmed">–</Text>}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'center' }}>
                          {total > 0
                            ? <Text size="sm" fw={700}>{total}</Text>
                            : <Text size="sm" c="dimmed">–</Text>}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>

      <Modal opened={opened} onClose={close} title="Create campaign" centered>
        <form onSubmit={handleCreateCampaign}>
          <Stack gap="sm">
            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
            />
            <Select
              label="Channel"
              data={CHANNEL_OPTIONS}
              value={channel}
              onChange={(value) => setChannel(value || 'email')}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" loading={creating}>
                Create
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={genOpened}
        onClose={() => { if (!generating) closeGen(); }}
        title={<Group gap={6}><IconSparkles size={16} /><Text fw={600}>Generate campaign</Text><Badge size="xs" variant="light" color="violet">Beta</Badge></Group>}
        centered
      >
        <form onSubmit={handleGenerateFlow}>
          <Stack gap="sm">
            <Select
              label="Template archetype"
              data={ARCHETYPE_OPTIONS}
              value={genArchetype}
              onChange={(value) => setGenArchetype(value || 'article-nurture')}
            />
            <Textarea
              label="Brief"
              description="Who is this for and what's the angle? e.g. 'Owners of allied-health clinics who struggle to fill last-minute cancellations.'"
              value={genBrief}
              onChange={(e) => setGenBrief(e.currentTarget.value)}
              minRows={3}
              autosize
              required
            />
            <Group grow>
              <NumberInput
                label="Touchpoints"
                min={1}
                max={8}
                value={genTouchCount}
                onChange={(value) => setGenTouchCount(typeof value === 'number' ? value : 3)}
              />
              <Select
                label="Channel"
                data={CHANNEL_OPTIONS}
                value={genChannel}
                onChange={(value) => setGenChannel(value || 'email')}
              />
            </Group>
            <TextInput
              label="End goal"
              value={genEndGoal}
              onChange={(e) => setGenEndGoal(e.currentTarget.value)}
            />
            {generating && (
              <Text size="xs" c="dimmed">Designing the flow — complex briefs can take several minutes. Progress is logged in the browser console and the API log.</Text>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeGen} disabled={generating}>
                Cancel
              </Button>
              <Button type="submit" color="violet" loading={generating} leftSection={<IconSparkles size={14} />}>
                Generate flow
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
}
